import { IgSegmentedControl } from "@intelligems/ig-theme";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
/**
 * The bodies an Advanced view switches between. `"primary"` is whatever the
 * domain's own body is — code for a custom rule, configuration for a template
 * one — so the control does not have to know which flavour it sits on.
 * `"runs"` is the Intelligems-only readout of recorded executions; a caller
 * opts into it with `showRuns`.
 */
export type WorkspaceBodyView = "primary" | "history" | "runs";

export interface WorkspaceViewSwitchProps {
  /** Label of the non-history segment ("Code", "Configuration", …). */
  primaryLabel: string;
  horizontalPadding?: number;
  topPadding?: number;
  bottomPadding?: number;
  /**
   * Adds the "Recent runs" segment. Off by default: the runs readout is an
   * Intelligems-staff view, and the caller owns the admin gate.
   */
  showRuns?: boolean;
  /**
   * Keeps the "Version history" segment. On by default, because every host had
   * it before this flag existed. The Agentic Logic rule page turns it off: its
   * version history is a top-level tab of the page around this workspace, and
   * two doors onto one list is how they drift apart.
   */
  showHistory?: boolean;
  value: WorkspaceBodyView;
  onChange: (value: WorkspaceBodyView) => void;
}

/**
 * The `primary | Version history` switch, shared by every rule flavour.
 *
 * Extracted from the code workspace because a rule's publish history is a
 * rule-level fact: shipping the control on one branch only is what left a
 * template-managed rule with no version history at the same route.
 */
export const WorkspaceViewSwitch = ({
  primaryLabel,
  horizontalPadding = 2,
  topPadding = 4,
  bottomPadding = 2,
  showRuns = false,
  showHistory = true,
  value,
  onChange,
}: WorkspaceViewSwitchProps) => {
  const options = [
    { value: "primary" as const, label: primaryLabel },
    ...(showHistory
      ? [{ value: "history" as const, label: "Version history" }]
      : []),
    ...(showRuns ? [{ value: "runs" as const, label: "Recent runs" }] : []),
  ];

  return (
    <Stack
      alignItems={"center"}
      justifyContent={"space-between"}
      direction="row"
      sx={{ px: horizontalPadding, pt: topPadding, pb: bottomPadding }}
    >
      {/* A one-option switch is a control that cannot do anything. The row
          itself stays either way: it carries the actions slot that the runs
          panel portals into, and dropping it would move the body under it. */}
      {options.length > 1 ? (
        <IgSegmentedControl<WorkspaceBodyView>
          ariaLabel="Workspace view"
          options={options}
          value={value}
          allowDeselect={false}
          onChange={(next) => {
            if (next !== null) {
              onChange(next);
            }
          }}
        />
      ) : (
        <span />
      )}
      <Box id={"workspace-view-actions"} />
    </Stack>
  );
};
